import fluencyPracticeThumbnail from 'src/assets/images/fluency-practice-thumbnail.svg';
import k5mClassThumbnail from 'src/assets/images/k-5-mclass-thumbnail.svg';
import {
  MSProduct,
  MSProductEntitlement,
} from 'src/interfaces/ms-product.types';
import { getAmpConfig } from 'src/services/amp-config.service';

import { Environments, getEnv } from './config.constants';

const MS_PRODUCT_DOMAINS = {
  MCLASS_LITERACY: 'mCLASS Literacy',
  MCLASS_READING: 'mCLASS Reading',
  MCLASS_LECTURA: 'mCLASS Lectura',
  MCLASS_BILITERACY: 'mCLASS Reading / Lectura',
  HUB: 'Hub',
  BOOST_READING: 'Boost Reading',
  BOOST_LECTURA: 'Boost Lectura',
  BOOST_BILITERACY: 'Boost Reading / Lectura',
  CLOSE_READING: 'Boost Close Reading',
  SCIENCE_APPS: 'Science Apps',
  FLUENCY_PRACTICE: 'Math Fluency Practice',
  POLYPAD: 'Polypad Manipulatives',
  FORMULA_WON: 'Formula Won',
  MATH: 'Math',
  TWELVE_A_DOZEN: 'Twelve a Dozen',
  NUMBER_JUMPER: 'Number Jumper',
  CONNECT_THE_BLOCKS: 'Connect the Blocks',
} as const;

const getCloseReadingUrl = () => {
  const closeReadingEnvUrls: Record<Environments, string> = {
    local: 'https://closereading-devci.poc.learning.amplify.com/#/home',
    devci: 'https://closereading-devci.poc.learning.amplify.com/#/home',
    staging: 'https://closereading-staging.stage.learning.amplify.com',
    prod: 'https://closereading.amplify.com',
  };
  const env = getEnv();
  return closeReadingEnvUrls[env];
};

export const getMSProductContentByEntitlement = (): Record<
  keyof MSProductEntitlement,
  MSProduct
> => {
  return {
    hasBoostReading: {
      id: 'boost-reading',
      domain: MS_PRODUCT_DOMAINS.BOOST_READING,
      url: getAmpConfig().amplifyReading,
      title: '',
      imageClassNames: '',
      isDomainBadgeHidden: false,
      imageUrl:
        'https://learning.amplify.com/m/23cba1177279dda8/original/Boost_K_5_StudentHome_Animals.png',
    },
    hasBoostLectura: {
      id: 'boost-lectura',
      domain: MS_PRODUCT_DOMAINS.BOOST_LECTURA,
      url: getAmpConfig().amplifyReading,
      title: '',
      imageClassNames: '',
      isDomainBadgeHidden: false,
      imageUrl:
        'https://learning.amplify.com/m/579a4a1edbfeb795/original/SH_Asset_Boost_Lectura.jpg',
    },
    hasBoostBiliteracy: {
      id: 'boost-biliteracy',
      domain: MS_PRODUCT_DOMAINS.BOOST_BILITERACY,
      url: getAmpConfig().amplifyReading,
      title: '',
      imageClassNames: '',
      isDomainBadgeHidden: false,
      imageUrl:
        'https://learning.amplify.com/m/23cba1177279dda8/original/Boost_K_5_StudentHome_Animals.png',
    },
    hasCloseReading: {
      id: 'close-reading',
      domain: MS_PRODUCT_DOMAINS.CLOSE_READING,
      url: getCloseReadingUrl(),
      title: 'Dive into the world of The Last Readers',
      imageClassNames: '',
      isDomainBadgeHidden: false,
      imageUrl:
        'https://learning.amplify.com/m/48c9810c2edb46d0/original/CloseReading_68_StudentHome.png',
    },
    hasMClass: {
      id: 'mclass',
      domain: MS_PRODUCT_DOMAINS.MCLASS_LITERACY,
      url: `${getAmpConfig().amplifyMClass}/student`,
      title: '',
      imageClassNames:
        'elementary-school-apps-card__thumbnail--old-mclass-literacy',
      isDomainBadgeHidden: false,
      imageUrl: k5mClassThumbnail,
    },
    hasMClassReading: {
      id: 'mclass-reading',
      domain: MS_PRODUCT_DOMAINS.MCLASS_READING,
      url: `${getAmpConfig().amplifyMClass}/student-assessment/`,
      title: '',
      imageClassNames: '',
      isDomainBadgeHidden: false,
      imageUrl: k5mClassThumbnail,
    },
    hasMClassLectura: {
      id: 'mclass-lectura',
      domain: MS_PRODUCT_DOMAINS.MCLASS_LECTURA,
      url: `${getAmpConfig().amplifyMClass}/student-assessment/`,
      title: 'Complete assessments',
      imageClassNames: '',
      isDomainBadgeHidden: false,
      imageUrl: k5mClassThumbnail,
    },
    hasMClassBiliteracy: {
      id: 'mclass-biliteracy',
      domain: MS_PRODUCT_DOMAINS.MCLASS_BILITERACY,
      url: `${getAmpConfig().amplifyMClass}/student-assessment/`,
      title: 'Complete assessments',
      imageClassNames: '',
      isDomainBadgeHidden: false,
      imageUrl: k5mClassThumbnail,
    },
    hasHub: {
      id: 'hub',
      domain: MS_PRODUCT_DOMAINS.HUB,
      url: `${getAmpConfig().amplifyHub}?no-redirect`,
      title: 'Click here to go to the Hub!',
      imageClassNames: '',
      isDomainBadgeHidden: true,
      imageUrl:
        'https://learning.amplify.com/m/112151815aaad842/original/SH_hub_robot_big.svg',
    },
    hasScienceApps: {
      id: 'science-apps',
      domain: MS_PRODUCT_DOMAINS.SCIENCE_APPS,
      domainMsView: 'Science',
      url: `${getAmpConfig().apps}/elementary?no-redirect`,
      title: 'Science Apps',
      imageClassNames: 'elementary-school-apps-card__thumbnail--science-apps',
      isDomainBadgeHidden: false,
      imageUrl:
        'https://learning.amplify.com/m/4520c1901ce96d7f/original/SH_SCI_apps_thumbnail.jpeg',
    },
    hasFluencyPractice: {
      id: 'fluency-practice',
      domain: MS_PRODUCT_DOMAINS.FLUENCY_PRACTICE,
      domainMsView: 'Math',
      url: 'https://fluency.amplify.com/?sso=1',
      title: 'Math Fluency Practice',
      imageClassNames: '',
      isDomainBadgeHidden: false,
      imageUrl: fluencyPracticeThumbnail,
    },
    hasPolypad: {
      id: 'polypad',
      domain: MS_PRODUCT_DOMAINS.POLYPAD,
      domainMsView: MS_PRODUCT_DOMAINS.MATH,
      url:
        getEnv() === 'devci'
          ? 'https://polypad-docker-devci.poc.learning.amplify.com/p/?sso=1'
          : 'https://polypad.amplify.com/p/?sso=1',
      title: 'Polypad Manipulatives',
      imageClassNames: '',
      isDomainBadgeHidden: false,
      imageUrl:
        'https://learning.amplify.com/m/554d2773f5f7e0d2/original/sh_polypad_icon.jpg',
    },
    hasFormulaWon: {
      id: 'formula-won',
      domain: MS_PRODUCT_DOMAINS.FORMULA_WON,
      domainMsView: MS_PRODUCT_DOMAINS.MATH,
      url: `${getAmpConfig().apps}/FormulaWon/`,
      title: 'Formula Won',
      imageClassNames: 'assignment-img--aligned-left',
      isDomainBadgeHidden: false,
      imageUrl:
        'https://learning.amplify.com/m/25ab8aa9c68f45b7/original/Boost_Math_Adventures_Formula_Won_Thumb.png',
    },
    hasTwelveADozen: {
      id: 'twelve-a-dozen',
      domain: MS_PRODUCT_DOMAINS.TWELVE_A_DOZEN,
      domainMsView: MS_PRODUCT_DOMAINS.MATH,
      url: `${getAmpConfig().apps}/TwelveADozen/`,
      title: 'Twelve a Dozen',
      imageClassNames: '',
      isDomainBadgeHidden: false,
      imageUrl:
        'https://learning.amplify.com/m/796a65861eff9b9c/original/ADM-Student-Home-Twelve.png',
    },
    hasNumberJumper: {
      id: 'number-jumper',
      domain: MS_PRODUCT_DOMAINS.NUMBER_JUMPER,
      domainMsView: MS_PRODUCT_DOMAINS.MATH,
      url: `${getAmpConfig().apps}/NumberJumper/`,
      title: 'Number Jumper',
      imageClassNames: '',
      isDomainBadgeHidden: false,
      imageUrl:
        'https://learning.amplify.com/m/2a2746e87a27a34b/original/ADM-Student-Home-Number-Jumper.png',
    },
    hasConnectTheBlocks: {
      id: 'connect-the-blocks',
      domain: MS_PRODUCT_DOMAINS.CONNECT_THE_BLOCKS,
      domainMsView: MS_PRODUCT_DOMAINS.MATH,
      url: `${getAmpConfig().apps}/ConnectTheBlocks/`,
      title: 'Connect the Blocks',
      imageClassNames: '',
      isDomainBadgeHidden: false,
      imageUrl:
        'https://learning.amplify.com/m/3215383a481c79ba/original/ADM-Student-Home-Connect-the-Blocks.png',
    },
  };
};

export const MS_CORE_PRODUCTS_ENTITLEMENTS_KEYS: (keyof MSProductEntitlement)[] =
  ['hasHub', 'hasScienceApps', 'hasFluencyPractice', 'hasPolypad'];
